import { Car, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { LeadVehicleDetails } from "@/types/lead";
import { useLanguage } from "@/context/LanguageContext";
import {
  transmissionTypeLabels,
  conditionGradeLabels,
  bodyTypeLabels,
  colorLabels,
  fuelTypeLabels,
} from "@/i18n/labels/vehicleLabels";
import { VehicleFuelType } from "@/types/enums";
import {
  leadUILabels,
  commonMiscLabels,
  vehicleCardUILabels,
  valuationLabels,
} from "@/i18n";
import { getEquipmentLabel } from "@/i18n/labels";
import { TransmissionType, ConditionGrade } from "@/types/enums";
import { formatMileage } from "@/utils/listingUtils";

type LeadVehicleDetailsWithHorsepower = LeadVehicleDetails & {
  horsepower?: number;
  engineSize?: string;
};

interface LeadCarDetailsProps {
  id: string;
  vehicleDetails: LeadVehicleDetailsWithHorsepower;
}

export const LeadCarDetails = ({ id, vehicleDetails }: LeadCarDetailsProps) => {
  const [showAllEquipment, setShowAllEquipment] = useState(false);
  const initialEquipmentCount = 6;
  const { language } = useLanguage();
  const uiLabels = leadUILabels(language);
  const commonLabels = commonMiscLabels(language);
  const vcLabels = vehicleCardUILabels(language);
  const valLabels = valuationLabels(language);

  // Safety check for vehicleDetails
  if (!vehicleDetails) {
    return (
      <div className="text-center text-muted-foreground p-4">
        {uiLabels.vehicleDetailsNotLoaded}
      </div>
    );
  }

  // Extract values from vehicleDetails
  const {
    regNumber,
    make,
    model,
    year,
    condition = "",
    variant = "",
    trim = "",
    mileage,
    engine,
    horsepower,
    engineSize,
    transmission,
    bodyType,
    color,
    equipment = [],
    electricDriveRange,
    fuelType,
  } = vehicleDetails;

  // Variant (equipment level) from AutoUncle; trim is a separate field
  const displayVariant = variant;
  const normalizedFuelType = (fuelType || "").toString().toUpperCase();
  const isElectric =
    normalizedFuelType === "EL" ||
    normalizedFuelType === "ELBIL" ||
    normalizedFuelType === "ELECTRIC";

  const displayedEquipment = showAllEquipment
    ? equipment
    : equipment.slice(0, initialEquipmentCount);

  return (
    <Card className="shadow-sm h-full">
      <CardHeader className="p-3">
        <CardTitle className="flex items-center gap-1.5 text-lg">
          <Car className="h-5 w-5" />
          {uiLabels.customerCar}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2 sm:p-4">
        <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3 sm:mb-4">
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">
              {commonLabels.registrationNumber}
            </span>
            <Badge variant="outline" className="text-xs h-5 sm:h-6">
              {regNumber}
            </Badge>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">
              {commonLabels.mileage}
            </span>
            <span>{formatMileage(mileage)} mil</span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">{commonLabels.engine}</span>
            <span className="text-right">{engine}</span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">{vcLabels.horsepower}</span>
            <span className="text-right">
              {horsepower || commonLabels.notSpecified}
            </span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">{vcLabels.engineSize}</span>
            <span className="text-right">
              {engineSize || commonLabels.notSpecified}
            </span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">{commonLabels.make}</span>
            <span>{make}</span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">{commonLabels.model}</span>
            <span>{model}</span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">
              {commonLabels.bodyType}
            </span>
            <span>
              {bodyType
                ? (bodyTypeLabels(language)[bodyType.toLowerCase()] ??
                  bodyType)
                : commonLabels.notSpecified}
            </span>
          </div>
          {/* Fuel type was missing from this panel entirely. The backend stores an enum
              (GASOLINE / PLUG_IN_HYBRID / ...), so render its display label rather than
              the raw value. */}
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">
              {commonLabels.fuelType}
            </span>
            <span>
              {fuelType
                ? (fuelTypeLabels(language)[
                    normalizedFuelType as VehicleFuelType
                  ] ?? fuelType)
                : commonLabels.notSpecified}
            </span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            {/* This row shows `variant` (the drivetrain descriptor), so it must be
                labelled Variant. It previously resolved the `equipmentLevel` key, which
                is the TRIM label - actively wrong now that a real Trim row sits below. */}
            <span className="text-muted-foreground">
              {valLabels.vehicleInfo?.variant ?? "Variant"}
            </span>
            <span className="text-right">
              {displayVariant || commonLabels.notSpecified}
            </span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">{vcLabels.trim}</span>
            <span className="text-right">
              {trim || commonLabels.notSpecified}
            </span>
          </div>
          {/* Show electric drive range only for electric vehicles */}
          {isElectric && (
            <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
              <span className="text-muted-foreground">
                {vcLabels.electricRangeWltp}
              </span>
              <span className="text-right">
                {electricDriveRange
                  ? `${electricDriveRange} km`
                  : commonLabels.notSpecified}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">{commonLabels.year}</span>
            <span>{year}</span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">
              {commonLabels.transmission}
            </span>
            <span>
              {transmissionTypeLabels(language)[
                transmission as TransmissionType
              ] ?? transmission}
            </span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">
              {commonLabels.condition}
            </span>
            <span>
              {condition
                ? (conditionGradeLabels(language)[
                    condition as ConditionGrade
                  ] ?? condition)
                : commonLabels.notSpecified}
            </span>
          </div>
          <div className="flex justify-between items-center border-b pb-1 sm:pb-2">
            <span className="text-muted-foreground">{commonLabels.color}</span>
            <span>
              {color
                ? (colorLabels(language)[color.toLowerCase()] ?? color)
                : commonLabels.notSpecified}
            </span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <h4 className="font-medium text-xs sm:text-sm">
              {commonLabels.equipment}
            </h4>
          </div>
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-1 sm:gap-1.5">
            {displayedEquipment.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-1 text-xs sm:text-sm"
              >
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="leading-tight truncate flex-1">
                  {getEquipmentLabel(item, language)}
                </span>
              </div>
            ))}
          </div>
          {equipment.length > initialEquipmentCount && (
            <Button
              variant="ghost"
              className="w-full mt-2 sm:mt-3 text-xs sm:text-sm h-7 sm:h-8"
              onClick={() => setShowAllEquipment(!showAllEquipment)}
            >
              {showAllEquipment
                ? commonLabels.showLess
                : `${commonLabels.showAll} (${equipment.length})`}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
